<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <b-card
      v-else
      class="rounded-lg ledger-dense-form"
    >
      <h4 class="mb-50">
        {{ isEdit ? $t('ledger_wh_edit') : $t('ledger_wh_new') }}
      </h4>
      <b-row>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_wh_code')">
            <b-form-input
              v-model="form.code"
              maxlength="32"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_wh_name')">
            <b-form-input v-model="form.name" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_wh_area')">
            <b-form-input v-model="form.area" />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          lg="3"
        >
          <b-form-group :label="$t('ledger_wh_description')">
            <b-form-input v-model="form.description" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
          class="d-flex align-items-end"
        >
          <b-form-checkbox
            v-model="form.in_use"
            class="mb-50"
          >
            {{ $t('ledger_wh_in_use') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canSave"
        @click="save"
      >
        {{ $t('ledger_assets_save') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BButton, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { createWarehouse, getWarehouse, updateWarehouse } from '@/api/accounting/warehouses'
import { oneFrom, apiErrorMessage } from '@/utils/ledger'

function emptyToNull(value) {
  const text = String(value || '').trim()
  return text || null
}

function blankForm() {
  return {
    code: '', name: '', description: '', area: '', in_use: true,
  }
}

export default {
  name: 'WarehouseForm',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BButton, BSpinner,
  },
  data() {
    return {
      loading: false,
      saving: false,
      baseline: null,
      form: blankForm(),
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    canSave() {
      const code = String(this.form.code || '').trim()
      const name = String(this.form.name || '').trim()
      if (!code || code.length > 32 || !name) return false
      if (this.isEdit && !Object.keys(this.changedPayload()).length) return false
      return true
    },
  },
  mounted() {
    if (this.isEdit) this.load()
  },
  methods: {
    normalized() {
      return {
        code: String(this.form.code || '').trim(),
        name: String(this.form.name || '').trim(),
        description: emptyToNull(this.form.description),
        area: emptyToNull(this.form.area),
        in_use: Boolean(this.form.in_use),
      }
    },
    changedPayload() {
      const next = this.normalized()
      const prev = this.baseline || {}
      const payload = {}
      Object.keys(next).forEach(key => {
        if (next[key] !== prev[key]) payload[key] = next[key]
      })
      return payload
    },
    createPayload() {
      const next = this.normalized()
      const payload = { code: next.code, name: next.name, in_use: next.in_use }
      if (next.description) payload.description = next.description
      if (next.area) payload.area = next.area
      return payload
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async load() {
      this.loading = true
      try {
        const warehouse = oneFrom(await getWarehouse(this.$route.params.id))
        this.form = {
          code: warehouse.code || '',
          name: warehouse.name || '',
          description: warehouse.description || '',
          area: warehouse.area == null ? '' : String(warehouse.area),
          in_use: Boolean(warehouse.in_use),
        }
        this.baseline = this.normalized()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const res = this.isEdit
          ? await updateWarehouse(this.$route.params.id, this.changedPayload())
          : await createWarehouse(this.createPayload())
        const warehouse = oneFrom(res)
        this.toast(this.$t('ledger_wh_saved'), 'success')
        const id = (warehouse && warehouse.id) || this.$route.params.id
        if (id) this.$router.push({ name: 'ledger-warehouses-view', params: { id } })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
.ledger-dense-form .card-body { padding: 0.75rem 1rem; }
.ledger-dense-form .form-group { margin-bottom: 0.35rem; }
.ledger-dense-form .form-group > label {
  font-size: 0.75rem;
  margin-bottom: 0.1rem;
}
.ledger-dense-form .form-control,
.ledger-dense-form .custom-select {
  height: 1.85rem;
  min-height: 1.85rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.8125rem;
}
</style>
